import { Injectable, OnDestroy } from '@angular/core';
import { AppConsts } from '@shared/AppConsts';
import { LocalStorageService } from '@shared/utils/local-storage.service';
import { AppAuthService } from './app-auth.service';

/**
 * Detects when a different user logs in on another browser tab.
 *
 * Problem: The app uses localForage which defaults to IndexedDB — NOT native localStorage.
 * The native `window storage` event only fires for localStorage writes, so it never
 * triggers when localForage/IndexedDB updates the token. That's why the storage-event
 * approach silently did nothing.
 *
 * Solution: Poll localForage every 2 seconds, compare the stored token's userId against
 * the userId that was active when this tab started. If they differ, a different user has
 * logged in on another tab — force a clean logout.
 */
@Injectable({ providedIn: 'root' })
export class CrossTabSessionService implements OnDestroy {
    private readonly TOKEN_KEY = AppConsts.authorization.encrptedAuthTokenName;
    private readonly POLL_INTERVAL_MS = 2000;

    private _originalUserId: number | null = null;
    private _pollTimer: any = null;
    private _logoutScheduled = false;

    constructor(
        private _localStorageService: LocalStorageService,
        private _appAuthService: AppAuthService,
    ) {}

    /**
     * Call once from AppComponent.ngOnInit() to start cross-tab monitoring.
     * Captures the current userId as baseline, then polls for changes.
     */
    initialize(): void {
        // Snapshot the userId that owns this tab's session.
        this._originalUserId = abp.session?.userId ?? null;

        // Nothing to watch if no one is logged in on this tab.
        if (!this._originalUserId) {
            return;
        }

        this._pollTimer = setInterval(() => this._checkSession(), this.POLL_INTERVAL_MS);
    }

    ngOnDestroy(): void {
        this._stopPolling();
    }

    private _checkSession(): void {
        if (this._logoutScheduled) {
            return;
        }

        this._localStorageService.getItem(this.TOKEN_KEY, (err: any, storedValue: any) => {
            if (err || this._logoutScheduled) {
                return;
            }

            // Token was removed from another tab (the other tab logged out).
            if (!storedValue || !storedValue.token) {
                this._triggerLogout();
                return;
            }

            // Decode the stored token to extract the userId claim and compare.
            try {
                const payload = this._parseJwtPayload(storedValue.token);
                const storedUserId = payload?.['http://www.aspnetboilerplate.com/identity/claims/userId'];

                if (storedUserId && Number(storedUserId) !== this._originalUserId) {
                    this._triggerLogout();
                }
            } catch {
                // Malformed token — treat as session conflict.
                this._triggerLogout();
            }
        });
    }

    private _triggerLogout(): void {
        if (this._logoutScheduled) {
            return;
        }
        this._logoutScheduled = true;
        this._stopPolling();

        abp.notify.warn(
            'You have been signed in with a different account in another tab. ' +
                'You will be redirected to the login page.',
            'Session Changed',
            { timer: 4000 },
        );

        setTimeout(() => {
            this._appAuthService.logoutInternal(true);
        }, 3000);
    }

    private _stopPolling(): void {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    }

    /**
     * Decodes the payload of a JWT without verifying signature.
     * Safe to use client-side — we only need the userId claim for comparison.
     */
    private _parseJwtPayload(token: string): Record<string, any> | null {
        try {
            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }
            const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const json = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join(''),
            );
            return JSON.parse(json);
        } catch {
            return null;
        }
    }
}
